import { Injectable, inject } from '@angular/core';
import { Country, SpokenLanguage } from '@app/core/models';
import { CustomService, I18nService, NavDataService, RolesService } from '@app/core/services';
import { AuthSessionService } from '@app/core/services/auth-session/auth-session.service';
import { NotificationsService } from '@app/core/services/notifications/notifications.service';
import { StaticValuesListService } from '@app/core/services/static-values-list/static-values-list.service';
import { TranslateService } from '@ngx-translate/core';
import { DrupalConstants, FileService, User, UserService } from 'ngx-drupal7-services';
import { Observable, catchError, of, throwError } from 'rxjs';

interface D8MappingField {
  fieldName: string;
  d7name?: string;
  needUnd?: boolean;
  needFunction?: (value: any) => any;
}

/**
 * Service to manage the user profile on Drupal 7.
 */
@Injectable({
  providedIn: 'root',
})
export class D7UserProfileService {
  private readonly mandatoryFields = ['field_birth_date', 'field_nationality'];
  private readonly rolesService: RolesService = inject(RolesService);
  private readonly notificationService: NotificationsService = inject(NotificationsService);
  private countries: Country[] = [];
  private spokenLanguages: SpokenLanguage[] = [];
  private allowLanguageD7 = ['en', 'fr', 'it', 'pt-br', 'de', 'es'];
  private d8MappingFields: D8MappingField[] = [
    {
      fieldName: 'field_first_name',
      needUnd: true,
    },
    {
      fieldName: 'field_surname',
      needUnd: true,
    },
    {
      fieldName: 'field_date_of_birth',
    },
    {
      fieldName: 'mail',
    },
    {
      fieldName: 'field_nationality',
      d7name: 'field_citizenship',
      needFunction: (country) => ({
        und: [{ value: this.countries.find((c) => c.code === country)?.name }],
      }),
    },
    {
      fieldName: 'field_phone',
      d7name: 'field_mobile_phone',
      needUnd: true,
    },
    {
      fieldName: 'field_birth_date',
      d7name: 'field_date_of_birth',
      needFunction: (birthField) => {
        birthField = birthField.split('-');
        return {
          und: [
            {
              value: {
                date: `${birthField[2]}/${birthField[1]}/${birthField[0]}`,
              },
            },
          ],
        };
      },
    },
  ];

  constructor(
    private userService: UserService,
    private fileService: FileService,
    private customService: CustomService,
    private staticValuesListService: StaticValuesListService,
    private navDataService: NavDataService,
    private i18nService: I18nService,
    private translateService: TranslateService,
    private authSessionService: AuthSessionService
  ) {
    this.staticValuesListService.getCountries().subscribe((countries) => {
      this.countries = countries;
    });
    this.staticValuesListService.getSpokenLanguages().subscribe((data) => {
      this.spokenLanguages = data;
    });
  }

  openProfile(): void {
    if (DrupalConstants.Connection && DrupalConstants.Connection.user) {
      this.navDataService.navigate(
        `https://www.ikointl.com/user/${DrupalConstants.Connection.user.uid}/edit?destination=/account/home`,
        true
      );
    } else {
      this.notificationService.notifyError(this.translateService.instant('Function not available'));
    }
  }

  removePicture(): void {
    fetch('/assets/images/blank-profile.png')
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File([blob], 'blankProfile.png', blob);
        this.uploadFile(file, 'user_picture');
      });
  }

  /**
   * Upload a  pircture file for user profile in Drupal 7.
   * @param file
   *  File to upload.
   * @param controlName
   *  Name of the control/field to update.
   */
  uploadFile(file: File, controlName: string): void {
    if (!file) {
      return;
    }
    this.customService.convertFileToBase64(file).subscribe({
      next: (fileData: string | ArrayBuffer) => {
        if (fileData instanceof ArrayBuffer) {
          fileData = new TextDecoder().decode(fileData);
        }
        const fileEntity: any = {
          file: this.customService.removeBase64(fileData),
          filename: file.name,
        };
        this.fileService.createFile(fileEntity).subscribe({
          next: (d7Data) => {
            if (controlName === 'field_user_cover') {
              return;
            }
            this.setProfilePicture(d7Data.fid);
          },
          error: (err) => {
            this.customService.sendLog(
              'error',
              'Error on uploading user picture on D7: ' + (err?.message ?? err.toString())
            );
          },
        });
      },
      error: (err) => {
        this.customService.sendLog(
          'error',
          'Error on converting file user picture for D7: ' + (err?.message ?? err.toString())
        );
      },
    });
  }

  /**
   * Updates the user profile on Drupal 7.
   *
   * @param values
   *   Values to update on Drupal 7.
   * @returns
   *   An observable with the updated user or void.
   */
  patch(values: any): Observable<User | void> {
    try {
      const form7Values = this.convertValues(values);
      if (Object.keys(form7Values).length === 0) {
        return of();
      }
      return this.userService.updateUser(form7Values).pipe(
        catchError((err) => {
          this.customService.sendLog(
            'error',
            'Error on saving user profile info on D7: ' + (err?.message ?? JSON.stringify(err))
          );

          throw throwError(() => new Error('errror update user profile on D7'));
        })
      );
    } catch (err) {
      console.error(err);
      return throwError(() => err);
    }
  }

  /**
   * Check if missing mandatory fields to save the user profile.
   * @param values
   *  Values that will be saved.
   * @returns
   *  An array with the missing fields or null if all mandatory fields are filled.
   */
  needsMandatoryFields(values: { [key: string]: any }): string[] | null {
    // Avoid to check mandatory fields for non-professional users.
    // Remark by Damien https://bitbucket.org/iko_intl/iko-app/pull-requests/2123#comment-452401693
    if (!this.rolesService.isProfessional) {
      return null;
    }
    const missingFields: string[] = [];
    for (const field of this.mandatoryFields) {
      if (
        !this.authSessionService.currentUserProfile?.[
          field as keyof typeof this.authSessionService.currentUserProfile
        ] &&
        !values[field]
      ) {
        missingFields.push(field);
      }
    }
    return missingFields.length ? missingFields : null;
  }

  private setProfilePicture(fileId: any): void {
    const user7Patch: any = {};
    user7Patch.picture = {
      fid: fileId,
    };

    user7Patch.user_picture = user7Patch.picture;
    user7Patch.field_user_avatar = this.userService.structureField(user7Patch.picture);
    user7Patch.field_citizenship = {
      und: [
        {
          value: this.countries.find((c) => c.code === this.authSessionService.currentUserProfile?.field_nationality)
            ?.name,
        },
      ],
    };
    user7Patch.uid = DrupalConstants.Connection.user.uid;
    this.userService.updateUser(user7Patch).subscribe({
      next: () => {
        this.authSessionService.initProfile().subscribe();
      },
      error: (err) => {
        this.customService.sendLog('error', 'Error on setting user picture on D7: ' + (err?.message ?? err.toString()));
      },
    });
  }

  /**
   * Converts the values from Drupal 8 structure to Drupal 7 structure.
   *
   * @param values
   *   Values from Drupal 8 to convert for Drupal 7.
   * @returns
   *  An object with the values converted for Durpal 7.
   */
  private convertValues(values: any): any {
    const originalValues = { ...values };
    const form7Values: any = {};

    for (const mappedField of this.d8MappingFields) {
      if (!originalValues[mappedField.fieldName]) {
        continue;
      }
      const fieldName = mappedField.d7name ?? mappedField.fieldName;
      if (mappedField.needFunction) {
        form7Values[fieldName] = mappedField.needFunction(originalValues[mappedField.fieldName]);
        continue;
      }
      if (mappedField.needUnd) {
        form7Values[fieldName] = { und: [{ value: originalValues[mappedField.fieldName] }] };
        continue;
      }
      form7Values[fieldName] = originalValues[mappedField.fieldName];
    }

    if (originalValues.field_gender === 'M' || originalValues.field_gender === 'F') {
      form7Values.field_sex = { und: originalValues.field_gender === 'M' ? 'Male' : 'Female' };
    }

    if (
      originalValues.field_address &&
      originalValues.field_address.length &&
      originalValues.field_address[0].address_line1
    ) {
      form7Values.field_address = { und: [{ value: originalValues.field_address[0].address_line1 }] };
      form7Values.field_city = { und: [{ value: originalValues.field_address[0].locality }] };
      form7Values.field_usercountry = {
        [DrupalConstants.Settings.language]: originalValues.field_address[0].country_code.toUpperCase(),
      };
    }

    if (originalValues.field_language_spoken && Array.isArray(originalValues.field_language_spoken)) {
      form7Values.field_language_spoken = {
        und: {
          select: originalValues.field_language_spoken
            .map((lang: string) => {
              const langSpoken = this.spokenLanguages.find((spoken) => spoken.lang_code === lang);
              if (!langSpoken) {
                return null;
              }
              if (langSpoken.name_en.indexOf(',') === -1) {
                return langSpoken.name_en;
              }
              return langSpoken.name_en.split(',')[0];
            })
            .filter((lang: string) => lang),
        },
      };
    }

    // if (originalValues.user_picture !== undefined && originalValues.user_picture !== null) {
    //   form7Values.picture = {
    //     fid: originalValues.user_picture,
    //   };

    //   form7Values.user_picture = form7Values.picture;
    //   form7Values.field_user_avatar = this.userService.structureField(form7Values.picture);
    //   form7Values.field_citizenship = {
    //     und: [
    //       {
    //         value: this.countries.find((c) => c.code === this.navDataService.currentUserProfile?.field_nationality)
    //           ?.name,
    //       },
    //     ],
    //   };
    // }

    form7Values.uid = DrupalConstants.Connection.user.uid;
    // save the data to the backend
    form7Values.language = this.i18nService.language.toLowerCase();
    if (this.allowLanguageD7.indexOf(form7Values.language) === -1) {
      form7Values.language = 'en';
    }
    const langEnglish = this.spokenLanguages.find((lang) => lang.lang_code === form7Values.language);
    if (langEnglish) {
      form7Values.field_language_preferred = {
        und: {
          select: langEnglish.name_en.split(',')[0],
        },
      };
    }
    return form7Values;
  }
}
